import { useEffect, useRef, useState } from 'react'
import { createPortal } from 'react-dom'
import { useTranslation } from 'react-i18next'
import cx from 'classnames'

import getAccessToken from 'hooks/getAccessToken'
import useTeamID from 'hooks/useTeamID'
import { listShares, createShare, retryShare, isAllowedFile, MAX_FILE_BYTES } from './api'
import styles from './SendToPatient.module.scss'

const formatBytes = bytes => {
  if (bytes >= 1024 * 1024) return `${(bytes / (1024 * 1024)).toFixed(1)}MB`
  return `${Math.ceil(bytes / 1024)}KB`
}

const STATUS_RETRYABLE = ['recording', 'notify_failed', 'conflict']

const StatusBadge = ({ status, testId = 'stp-status' }) => {
  const { t } = useTranslation()
  const labels = {
    sent: t('share_status_sent', 'Enviado ✓'),
    notify_failed: t('share_status_notify_failed', 'Guardado — falta notificar'),
    recording: t('share_status_recording', 'Guardado parcial — reintentar'),
    conflict: t('share_status_conflict', 'No se pudo notificar'),
    failed: t('share_status_failed', 'Falló — rehacer el envío'),
  }
  return (
    <span
      data-testid={testId}
      data-status={status}
      className={cx(styles.badge, {
        [styles.badgeSuccess]: status === 'sent',
        [styles.badgeWarning]: ['notify_failed', 'recording'].includes(status),
        [styles.badgeDanger]: ['conflict', 'failed'].includes(status),
      })}
    >
      {labels[status] || status}
    </span>
  )
}

const SendToPatientModal = ({ event, onClose, onSent }) => {
  const { t } = useTranslation()
  const teamID = useTeamID()
  const token = getAccessToken()
  const fileInputRef = useRef(null)

  // Array combinado {file, title} en vez de dos arrays paralelos por
  // índice (V2, card "Envío de archivos a pacientes") — evita
  // desincronizar título/archivo al quitar uno del medio de la lista.
  const [attachments, setAttachments] = useState([])
  const [message, setMessage] = useState('')
  const [fileError, setFileError] = useState(null)
  const [sending, setSending] = useState(false)
  const [result, setResult] = useState(null)
  const [error, setError] = useState(null)
  const [history, setHistory] = useState(null)
  const [historyError, setHistoryError] = useState(false)
  const [retrying, setRetrying] = useState(null)
  const nextAttachmentId = useRef(0)
  const modalRef = useRef(null)

  // Hallazgo real de review (card): cerrar (Escape/backdrop/X) no cancelaba
  // un envío/reintento en curso — el fetch seguía en vuelo, y al resolver
  // llamaba setState sobre un componente ya desmontado sin que el usuario
  // se enterara de que el archivo sí había llegado al paciente.
  const busy = sending || !!retrying
  const mountedRef = useRef(true)
  useEffect(() => () => { mountedRef.current = false }, [])

  const patientEmail = event.consumer?.email

  const refreshHistory = async accessToken => {
    try {
      const rows = await listShares({ token: accessToken, teamID, eventID: event.id })
      if (!mountedRef.current) return
      setHistory(rows)
      setHistoryError(false)
    } catch {
      // distinguible de "sin envíos previos": el servicio no respondió
      if (!mountedRef.current) return
      setHistory([])
      setHistoryError(true)
    }
  }

  useEffect(() => {
    if (token.value && teamID) refreshHistory(token.value)
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [token.value, teamID])

  // Ref (no state) para que el listener, montado una sola vez, siempre lea
  // el valor más reciente de `busy` sin tener que re-suscribirse.
  const busyRef = useRef(busy)
  busyRef.current = busy

  // Accesibilidad mínima (hallazgo real de review, ausente desde V1 — mismo
  // patrón ya establecido en Percentiles.js/Facturar.js para modales full-
  // screen de la HCE): foco inicial en el diálogo + restaurarlo al trigger
  // al cerrar, en vez de dejarlo caer en <body>.
  useEffect(() => {
    const previouslyFocused = document.activeElement
    modalRef.current?.focus()
    const onKeyDown = e => {
      if (e.key === 'Escape' && !busyRef.current) onClose()
    }
    document.addEventListener('keydown', onKeyDown)
    return () => {
      document.removeEventListener('keydown', onKeyDown)
      if (previouslyFocused instanceof HTMLElement) previouslyFocused.focus()
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [])

  const onSelectFiles = selected => {
    setFileError(null)
    const incoming = Array.from(selected || [])
    const rejected = []
    const accepted = incoming.filter(file => {
      if (!isAllowedFile(file)) {
        rejected.push(`${file.name} (${t('file_type_not_allowed', 'tipo no permitido')})`)
        return false
      }
      if (file.size > MAX_FILE_BYTES) {
        rejected.push(`${file.name} (${t('file_too_big', 'supera 25MB')})`)
        return false
      }
      return true
    })
    if (rejected.length) {
      setFileError(t('files_rejected', 'No se adjuntaron: {{files}}', { files: rejected.join(', ') }))
    }
    // Título prellenado con el nombre original — editable antes de enviar
    // (card: "una vez cargado un archivo, agregar un botón de editar que
    // disponibilice un input para permitir modificar el nombre"). Id propio
    // por archivo (no derivado de nombre+índice, que se corría al borrar un
    // ítem del medio y causaba remount/pérdida de foco del título — hallazgo
    // real de review, confirmado también en web).
    setAttachments(current => [
      ...current,
      ...accepted.map(file => ({ id: `att-${nextAttachmentId.current++}`, file, title: file.name })),
    ])
    if (fileInputRef.current) fileInputRef.current.value = ''
  }

  const removeFile = id => setAttachments(current => current.filter(a => a.id !== id))
  const setTitle = (id, title) => setAttachments(current => current.map(a => (a.id === id ? { ...a, title } : a)))

  const submit = async () => {
    if (sending || !token.value) return
    setError(null)
    setResult(null)
    setSending(true)
    try {
      const share = await createShare({
        token: token.value,
        teamID,
        eventID: event.id,
        files: attachments.map(a => a.file),
        titles: attachments.map(a => a.title.trim()),
        message: message.trim(),
      })
      if (!mountedRef.current) return
      setResult(share)
      if (share.status === 'sent') {
        setAttachments([])
        setMessage('')
        onSent?.()
      }
      refreshHistory(token.value)
    } catch (requestError) {
      if (mountedRef.current) setError(requestError)
    } finally {
      if (mountedRef.current) setSending(false)
    }
  }

  const retry = async shareID => {
    if (retrying || !token.value) return
    setRetrying(shareID)
    setError(null)
    try {
      const share = await retryShare({ token: token.value, teamID, shareID })
      if (!mountedRef.current) return
      setResult(share)
      if (share.status === 'sent') {
        setAttachments([])
        setMessage('')
        onSent?.()
      }
      refreshHistory(token.value)
    } catch (requestError) {
      if (mountedRef.current) setError(requestError)
    } finally {
      if (mountedRef.current) setRetrying(null)
    }
  }

  // Con un envío pendiente de reintento, el único camino es "Reintentar": el submit
  // principal crearía un share NUEVO re-subiendo los archivos (duplicado para el paciente).
  const pendingRetry = !!result && STATUS_RETRYABLE.includes(result.status)
  const canSubmit = !sending && !pendingRetry && token.value && (attachments.length > 0 || message.trim().length > 0)

  const modal = (
    <>
      <div data-testid="stp-backdrop" className={styles.backdrop} onClick={() => { if (!busy) onClose() }} />
      <div ref={modalRef} tabIndex={-1} className={styles.dialog} role="dialog" aria-modal="true" aria-labelledby="stp-title" data-testid="stp-modal">
        <div className={styles.card} onClick={e => e.stopPropagation()}>
          <div className={styles.cardInner}>
            <div className={styles.header}>
              <h3 className={styles.title} id="stp-title">{t('send_to_patient', 'Enviar al paciente')}</h3>
              <button type="button" className={styles.closeBtn} onClick={onClose} disabled={busy} aria-label={t('close', 'Cerrar')}>
                <span className="material-symbols-outlined" style={{ fontSize: 20 }}>close</span>
              </button>
            </div>

            <p className={styles.helper}>
              {t('send_to_patient_helper', 'El paciente lo recibe por email y lo ve en su cuenta de crontu, vinculado a este turno.')}
              {patientEmail && (
                <>
                  {' '}
                  <span className={styles.email} data-testid="stp-patient-email">{patientEmail}</span>
                </>
              )}
            </p>

            <div>
              <input
                ref={fileInputRef}
                data-testid="stp-file-input"
                id="stp-file-input"
                className="d-none"
                type="file"
                multiple
                disabled={pendingRetry}
                accept=".pdf,.jpg,.jpeg,.png,.dcm,application/pdf,image/jpeg,image/png,application/dicom"
                onChange={e => onSelectFiles(e.target.files)}
              />
              <label htmlFor="stp-file-input" className={styles.attachBtn} style={pendingRetry ? { pointerEvents: 'none', opacity: 0.5 } : undefined}>
                <span className="material-symbols-outlined" style={{ fontSize: 18 }}>attach_file</span>
                {t('attach_files', 'Adjuntar archivos')}
              </label>
              <span className={styles.attachHint}>{t('attach_hint', 'PDF, JPG, PNG o DICOM — máx. 25MB c/u')}</span>

              {attachments.length > 0 && (
                <ul className={styles.fileList} data-testid="stp-file-list">
                  {attachments.map(({ id, file, title }) => (
                    <li key={id} className={styles.fileItem}>
                      <div className={styles.fileItemTop}>
                        <span className={styles.fileName}>{file.name}</span>
                        <span className={styles.fileSize}>{formatBytes(file.size)}</span>
                        <button
                          type="button"
                          className={styles.fileRemove}
                          onClick={() => removeFile(id)}
                          aria-label={t('remove_file', 'Quitar {{file}}', { file: file.name })}
                        >
                          <span className="material-symbols-outlined" style={{ fontSize: 16 }}>close</span>
                        </button>
                      </div>
                      {/* Hallazgo real de review (card): sin esto el archivo
                          quedaba con el nombre crudo del picker (ej. una
                          foto de WhatsApp sin nombre identificatorio). */}
                      <input
                        type="text"
                        className="form-control form-control-sm"
                        aria-label={t('file_title', 'Título para {{file}}', { file: file.name })}
                        placeholder={t('file_title_placeholder', 'Título del archivo')}
                        maxLength={255}
                        value={title}
                        onChange={e => setTitle(id, e.target.value)}
                      />
                    </li>
                  ))}
                </ul>
              )}
              {fileError && <p className={styles.fileError} data-testid="stp-file-error">{fileError}</p>}
            </div>

            <textarea
              data-testid="stp-message"
              className="form-control"
              rows={3}
              disabled={pendingRetry}
              placeholder={t('send_to_patient_message', 'Indicaciones para el paciente (opcional)')}
              value={message}
              onChange={e => setMessage(e.target.value)}
            />

            {result && (
              // aria-live: hallazgo real de review — sin esto, el éxito/falla
              // del envío de un documento clínico solo se ve si el usuario
              // tiene la vista puesta acá, y no se anuncia a un lector de
              // pantalla que haya seguido explorando el resto del modal.
              <div className={styles.result} data-testid="stp-result" role="status" aria-live="polite">
                <StatusBadge status={result.status} />
                {result.status === 'sent' && (
                  <p className={styles.resultText}>{t('share_sent_detail', 'El paciente va a recibir un email con el link.')}</p>
                )}
                {result.status === 'notify_failed' && (
                  <p className={styles.resultText}>{t('share_notify_failed_detail', 'El envío quedó guardado y no se pierde: falta avisar a crontu. Reintentá la notificación.')}</p>
                )}
                {result.status === 'recording' && (
                  <p className={styles.resultText}>{t('share_recording_detail', 'Los archivos se subieron pero no terminó el registro. Reintentá.')}</p>
                )}
                {['conflict', 'failed'].includes(result.status) && result.error?.message && (
                  <p className={styles.resultText}>{result.error.message}</p>
                )}
                {STATUS_RETRYABLE.includes(result.status) && (
                  <button
                    type="button"
                    data-testid="stp-retry"
                    className={styles.btnRetry}
                    disabled={!!retrying}
                    onClick={() => retry(result.id)}
                  >
                    {retrying === result.id && <span className="spinner-border spinner-border-sm" aria-hidden="true" />}
                    {t('retry_notification', 'Reintentar')}
                  </button>
                )}
              </div>
            )}

            {error && (
              <p className={styles.fileError} data-testid="stp-error">
                {error.message}
              </p>
            )}

            {historyError && (
              <p className={styles.fileError} data-testid="stp-history-error">
                {t('share_history_error', 'No pudimos cargar el historial de envíos de este turno.')}
              </p>
            )}

            {history?.length > 0 && (
              <div data-testid="stp-history">
                <h4 className={styles.historyTitle}>{t('share_history', 'Envíos de este turno')}</h4>
                <ul className={styles.historyList}>
                  {history.map(share => (
                    <li key={share.id} className={styles.historyItem}>
                      <div className={styles.historyLeft}>
                        <span className={styles.historyDate}>
                          {new Date(share.createdAt).toLocaleString()}
                        </span>
                        {/* Hallazgo real de review (card): antes solo se
                            mostraba la CANTIDAD ("2 archivo(s)") — se pide
                            ver el nombre real de cada archivo. */}
                        <span className={styles.historyFiles}>
                          {[
                            share.files?.length > 0 && share.files.map(f => f.title || f.filename).join(', '),
                            share.message && t('with_note', 'con indicación'),
                          ].filter(Boolean).join(' · ')}
                        </span>
                      </div>
                      <StatusBadge status={share.status} testId="stp-history-status" />
                      {STATUS_RETRYABLE.includes(share.status) && share.id !== result?.id && (
                        <button
                          type="button"
                          data-testid="stp-history-retry"
                          className={styles.btnRetry}
                          disabled={!!retrying}
                          onClick={() => retry(share.id)}
                        >
                          {retrying === share.id && <span className="spinner-border spinner-border-sm" aria-hidden="true" />}
                          {t('retry_notification', 'Reintentar')}
                        </button>
                      )}
                    </li>
                  ))}
                </ul>
              </div>
            )}

            <div className={styles.footer}>
              <button type="button" className={styles.btnBack} onClick={onClose} disabled={busy}>
                {t('close', 'Cerrar')}
              </button>
              <button
                type="button"
                data-testid="stp-submit"
                className={styles.btnConfirm}
                disabled={!canSubmit}
                onClick={submit}
              >
                {sending && <span className="spinner-border spinner-border-sm" aria-hidden="true" />}
                {sending ? t('sending', 'Enviando…') : t('send_to_patient_action', 'Enviar al paciente')}
              </button>
            </div>
          </div>
        </div>
      </div>
    </>
  )

  return createPortal(modal, document.body)
}

export default SendToPatientModal
